import type {
  AudioFilterDefinition,
  FilterDefinition,
  SourceTypeDefinition,
  TransitionDefinition,
  VideoFilterDefinition,
} from './definitions';
import type { SettingsBag } from './model';
import type { Unsubscribe } from './services';

/**
 * Single lookup table for every extensible type. Built-in modules and plugins
 * register through the same methods; each returns a function that removes the
 * registration again.
 */
export class Registry {
  private readonly sources = new Map<string, SourceTypeDefinition>();
  private readonly filters = new Map<string, FilterDefinition>();
  private readonly transitions = new Map<string, TransitionDefinition>();
  private readonly listeners = new Set<() => void>();

  registerSource<S extends SettingsBag>(def: SourceTypeDefinition<S>): Unsubscribe {
    return this.add(this.sources, def.kind, def as unknown as SourceTypeDefinition, 'source');
  }

  registerFilter<S extends SettingsBag>(def: VideoFilterDefinition<S> | AudioFilterDefinition<S>): Unsubscribe {
    return this.add(this.filters, def.kind, def as unknown as FilterDefinition, 'filter');
  }

  registerTransition<S extends SettingsBag>(def: TransitionDefinition<S>): Unsubscribe {
    return this.add(this.transitions, def.kind, def as unknown as TransitionDefinition, 'transition');
  }

  getSource(kind: string): SourceTypeDefinition | undefined {
    return this.sources.get(kind);
  }

  getFilter(kind: string): FilterDefinition | undefined {
    return this.filters.get(kind);
  }

  getVideoFilter(kind: string): VideoFilterDefinition | undefined {
    const def = this.filters.get(kind);
    return def?.type === 'video' ? def : undefined;
  }

  getAudioFilter(kind: string): AudioFilterDefinition | undefined {
    const def = this.filters.get(kind);
    return def?.type === 'audio' ? def : undefined;
  }

  getTransition(kind: string): TransitionDefinition | undefined {
    return this.transitions.get(kind);
  }

  listSources(): SourceTypeDefinition[] {
    return [...this.sources.values()];
  }

  listFilters(type?: 'video' | 'audio'): FilterDefinition[] {
    const all = [...this.filters.values()];
    return type ? all.filter((f) => f.type === type) : all;
  }

  listTransitions(): TransitionDefinition[] {
    return [...this.transitions.values()];
  }

  /** Fires after any registration or removal. */
  subscribe(listener: () => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private add<T>(map: Map<string, T>, kind: string, def: T, what: string): Unsubscribe {
    if (map.has(kind)) throw new Error(`Duplicate ${what} kind "${kind}"`);
    map.set(kind, def);
    this.notify();
    return () => {
      if (map.get(kind) === def) {
        map.delete(kind);
        this.notify();
      }
    };
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener();
  }
}
